import { TasksResponseType } from '@/api/backend/types';
import { ResponsiveDataView } from '@/components/responsive-data-view';
import { Button, Card, DataTable, DataTableColumnType, Link } from '@opaz/ui-components';

import { getTasks } from '@/api/backend/workflow';
import { TaskStatusBadge } from '@/components/task-status-badge';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

export function ActiveTasksCard() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const { data: page, isLoading: isTasksLoading } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => {
      return getTasks(
        {
          start: 0,
          size: 5,
          state: 'created',
        },
        null,
      );
    },
  });

  const columns: DataTableColumnType<TasksResponseType>[] = [
    {
      title: t('dashboard.tasks.headings.id'),
      render: (record: TasksResponseType) => {
        let taskName = record.id;
        const match = record.processInstanceName?.match(/^APP-\d{7}/);

        if (match && match[0]) {
          taskName = match[0];
        }

        return (
          <Link
            onClick={() => {
              navigate({
                to: `/processes/$processInstanceId/task/$taskId`,
                params: { processInstanceId: record.processInstanceId, taskId: record.id },
                search: {
                  responseToken: undefined,
                },
              });
            }}
            className="text-start w-[120px]"
          >
            {taskName}
          </Link>
        );
      },
    },
    {
      title: t('dashboard.tasks.headings.task'),
      render: (record: TasksResponseType) => (
        <span className="font-bold text-[#113561]">{record.translations?.[i18n.language]?.name || record.name}</span>
      ),
    },
    {
      title: t('dashboard.tasks.headings.application'),
      render: (record: TasksResponseType) => (
        <>
          <Link
            onClick={() => {
              navigate({
                to: `/processes/$processInstanceId`,
                params: { processInstanceId: record.processInstanceId },
                search: { from: undefined, version: undefined },
              });
            }}
            className="font-bold text-start"
          >
            {record.translations?.[i18n.language]?.parentScopeDefinitionName || record.processDefinitionName}
          </Link>
          <br />
        </>
      ),
    },
    {
      title: t('dashboard.tasks.headings.dueDate'),
      align: 'end',
      render: (record: TasksResponseType) => {
        return <TaskStatusBadge endTime={record.endTime} dueDate={record.dueDate} />;
      },
    },
  ];

  return (
    <Card className="w-full p-5 shadow-lg border-none">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold mb-6">{t('dashboard.activeTasks.title')}</h2>

        <Button
          variant="secondary"
          onClick={() =>
            navigate({
              to: '/tasks',
            })
          }
        >
          {t('dashboard.activeTasks.allTasks')}
        </Button>
      </div>

      <ResponsiveDataView
        columns={columns}
        data={page?.data?.length ? [...page.data] : []}
        isLoading={isTasksLoading}
        emptyMessage={t('common.table.emptyState')}
        getRowKey={(record) => record.id}
      >
        <DataTable
          columns={columns}
          pagination={false}
          dataSource={page?.data?.length ? [...page.data] : []}
          isLoading={isTasksLoading}
          translations={{
            noData: t('common.table.emptyState'),
          }}
        />
      </ResponsiveDataView>
    </Card>
  );
}
